import clsx from 'clsx';
import { FC, HTMLProps, isValidElement, PropsWithChildren, ReactElement, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeColorPaletteAsCssTokens } from '../../hooks/use-theme-color-palette-as-css-tokens';
import { ThemeColors, ThemeSignalColor } from '../../theme';
import { Icon, IconProps } from '../icon';
import { IconButton } from '../icon-button';
import { Stack } from '../stack';
import { Typofallback } from '../typofallback';
import classes from './info-box.module.scss';

export type InfoBoxProps = PropsWithChildren & Omit<HTMLProps<HTMLDivElement>, 'color' | 'ref' | 'suffix'> & {
  /**
   * Custom close handler
   */
  onClose?: () => void;

  /**
   * Color palette
   */
  color?: ThemeColors;

  /**
   * Custom icon
   */
  icon?: IconProps['icon'] | ReactElement | false;

  /**
   * Custom headline element
   */
  headline?: ReactNode;

  /**
   * Render custom actions on the right hand sinde
   */
  actions?: ReactNode;

  /**
   * Represents the positioning behavior of an element.
   *
   * The `position` property determines how the element is positioned
   * within its containing layout.
   *
   * Possible values:
   * - `'fixed'`: The element is positioned according to the normal flow of the document.
   * - `'sticky'`: The element is treated as "relative" until it crosses a certain threshold,
   *   after which it is treated as "fixed" based on the scrolling position.
   */
  position?: 'fixed' | 'sticky';

  /**
   * An optional ReactNode that can be appended after the main content.
   * This is typically used to display additional elements, such as icons or text, at the end of a component.
   */
  suffix?: ReactNode;

  /**
   * Specifies the alignment positioning for an element or component.
   * Acceptable values are:
   * - 'top': Aligns the content to the top.
   * - 'center': Aligns the content to the center.
   * This property is optional.
   */
  align?: 'top' | 'center';

  /**
   *
   */
  withLinkFormatting?: boolean;
};

const Color_Icon_Map: Record<ThemeSignalColor, IconProps['icon']> = {
  info: ['fas', 'info-circle'],
  warning: ['fas', 'exclamation-circle'],
  danger: ['fas', 'times-circle'],
  success: ['fas', 'check-circle']
};

// @deprecated use MessageBox
export const InfoBox: FC<InfoBoxProps> = ({
  color = 'info',
  onClose,
  children,
  icon,
  headline,
  actions,
  position,
  suffix,
  align,
  withLinkFormatting = true,
  ...props
}) => {
  const [t] = useTranslation();
  const styles = useThemeColorPaletteAsCssTokens('--info-box', color, [
    'main',
    'ultra-light',
    'dark'
  ]);

  const iconToRender = useMemo(() => {
    if (icon) {
      return icon;
    }

    return Color_Icon_Map[color] ?? ['fas', 'info-circle'];
  }, [color, icon]);

  return (
    <div
      {...props}
      role={['danger', 'warning'].includes(color) ? 'alert' : 'status'}
      className={clsx('info-box-component', classes.root, props.className, {
        [classes.isStatic]: position === 'fixed',
        [classes.isSticky]: position === 'sticky',
        [classes.isCentered]: align === 'center',
        [classes.hasIcon]: icon !== false,
        [classes.linkFormatting]: withLinkFormatting
      })}
      style={{ ...styles, ...props.style }}
    >
      <div className={classes.wrapper}>
        {icon !== false && <div className={classes.prefix}>
          {isValidElement(icon) ? icon : <Icon className={classes.icon} icon={iconToRender as IconProps['icon']} />}
        </div>}
        <div className={classes.children}>
          {headline && (
            <Typofallback bold as="p" noWrap>
              {headline}
            </Typofallback>
          )}
          {children && (
            <Stack direction="column">
              <Typofallback as="p">{children}</Typofallback>
            </Stack>
          )}
          <div className={classes.actions}>{actions}</div>
        </div>

        <Stack className={classes.suffix} align="end" spacing={0.5} crossAxis="center">
          {suffix}
          {onClose && (
            <div className={classes.closeBtnWrapper}>
              <IconButton
                label={t('common.close')}
                className={classes.closeBtn}
                variant="plain"
                data-testid="close-btn"
                color={color}
                onClick={onClose}
                size="md"
              >
                <Icon icon={['fas', 'times']} />
              </IconButton>
            </div>
          )}
        </Stack>
      </div>
    </div>
  );
};